import { useEffect, useMemo, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { calendarService, CalendarEvent, CalendarEventType, UpsertCalendarEventInput, VirtualCalendarEntry } from '@/services/calendar.service';
import { useAuth } from '@/hooks/useAuth';
import { Plus, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Trash2, MapPin, User } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

const TYPE_LABELS: Record<CalendarEventType, string> = {
  appointment: 'Appointment', follow_up: 'Follow-up', meeting: 'Meeting',
  call: 'Call', task: 'Task', reminder: 'Reminder', other: 'Other',
};

const TYPE_COLORS: Record<CalendarEventType, string> = {
  appointment: 'bg-info-soft text-info border-info/30',
  follow_up:   'bg-warning-soft text-warning border-warning/30',
  meeting:     'bg-primary-soft text-primary border-primary/30',
  call:        'bg-success-soft text-success border-success/30',
  task:        'bg-surface-alt text-text-secondary border-border',
  reminder:    'bg-danger-soft text-danger border-danger/30',
  other:       'bg-surface-alt text-text-muted border-border',
};

function startOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999); }
function startOfDay(d: Date) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
function addDays(d: Date, n: number) { return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n); }
function sameDay(a: Date, b: Date) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function isoLocal(d: Date) { const tz = d.getTimezoneOffset(); const local = new Date(d.getTime() - tz * 60000); return local.toISOString().slice(0, 16); }

interface DayCell { date: Date; inMonth: boolean; events: CalendarEvent[]; }

function buildMonthGrid(monthAnchor: Date, events: CalendarEvent[]): DayCell[] {
  const first = startOfMonth(monthAnchor);
  const last = endOfMonth(monthAnchor);
  const startWeekday = first.getDay(); // 0 = Sun
  const gridStart = addDays(first, -startWeekday);
  const cells: DayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = addDays(gridStart, i);
    const dayEvents = events.filter((e) => sameDay(new Date(e.startsAt), d));
    cells.push({ date: d, inMonth: d >= first && d <= last, events: dayEvents });
  }
  return cells;
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [monthAnchor, setMonthAnchor] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [virtual, setVirtual] = useState<VirtualCalendarEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [mineOnly, setMineOnly] = useState(false);
  const [showDeals, setShowDeals] = useState(true);
  const [showTasks, setShowTasks] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CalendarEvent | null>(null);
  const [busy, setBusy] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [feedUrl, setFeedUrl] = useState<string | null>(null);
  const [feedRevealed, setFeedRevealed] = useState(false);

  // Form state
  const [type, setType] = useState<CalendarEventType>('appointment');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await calendarService.listCombined({
        from: startOfMonth(monthAnchor).toISOString(),
        to: endOfMonth(monthAnchor).toISOString(),
        assigneeUserUid: mineOnly ? user?.userUid : undefined,
      });
      setEvents(r.events);
      setVirtual(r.virtual);
    } catch { /* interceptor */ } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [monthAnchor, mineOnly]);

  // Treat virtual entries (deal close / task due) as additional events for
  // the grid and upcoming list. Filter respects per-source toggles.
  const filteredVirtual = useMemo(() =>
    virtual.filter((v) => (v.kind === 'deal_close' ? showDeals : showTasks)),
    [virtual, showDeals, showTasks],
  );
  const allEvents = useMemo<CalendarEvent[]>(() =>
    [...events, ...(filteredVirtual as unknown as CalendarEvent[])],
    [events, filteredVirtual],
  );

  const grid = useMemo(() => buildMonthGrid(monthAnchor, allEvents), [monthAnchor, allEvents]);
  const monthLabel = monthAnchor.toLocaleDateString([], { month: 'long', year: 'numeric' });
  const upcoming = useMemo(() => allEvents.filter((e) => new Date(e.startsAt) >= startOfDay(new Date())).slice(0, 8), [allEvents]);

  const openNewModal = (date?: Date) => {
    setEditing(null);
    setType('appointment');
    setTitle('');
    setDescription('');
    setLocation('');
    const d = date ?? new Date();
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 10, 0);
    const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 11, 0);
    setStartsAt(isoLocal(start));
    setEndsAt(isoLocal(end));
    setModalOpen(true);
  };

  const openEditModal = (e: CalendarEvent) => {
    setEditing(e);
    setType(e.type);
    setTitle(e.title);
    setDescription(e.description ?? '');
    setLocation(e.location ?? '');
    setStartsAt(isoLocal(new Date(e.startsAt)));
    setEndsAt(isoLocal(new Date(e.endsAt)));
    setModalOpen(true);
  };

  const save = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (!startsAt || !endsAt) { toast.error('Start and end times are required'); return; }
    setBusy(true);
    try {
      const payload: UpsertCalendarEventInput = {
        type,
        title: title.trim(),
        description: description.trim() || undefined,
        location: location.trim() || undefined,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
      };
      if (editing) {
        await calendarService.update(editing.eventUid, payload);
        toast.success('Event updated');
      } else {
        await calendarService.create(payload);
        toast.success('Event created');
      }
      setModalOpen(false);
      refresh();
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm(`Delete "${editing.title}"?`)) return; // eslint-disable-line no-restricted-globals, no-alert
    setBusy(true);
    try {
      await calendarService.remove(editing.eventUid);
      toast.success('Event removed');
      setModalOpen(false);
      refresh();
    } finally { setBusy(false); }
  };

  return (
    <>
      <Topbar
        title="Calendar"
        breadcrumb="Schedule"
        description="Appointments, follow-ups, and reminders."
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowDeals((v) => !v)}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors',
                showDeals ? 'border-warning/40 bg-warning-soft text-warning' : 'border-border text-text-muted hover:bg-surface-alt',
              )}
              title="Toggle deal close dates on the calendar"
            >
              Deal closes
            </button>
            <button
              onClick={() => setShowTasks((v) => !v)}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors',
                showTasks ? 'border-info/40 bg-info-soft text-info' : 'border-border text-text-muted hover:bg-surface-alt',
              )}
              title="Toggle task due dates on the calendar"
            >
              Task dues
            </button>
            <button
              onClick={() => setMineOnly((v) => !v)}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors',
                mineOnly ? 'border-primary bg-primary-soft text-primary' : 'border-border text-text-secondary hover:bg-surface-alt',
              )}
            >
              <User size={12} /> Mine only
            </button>
            <button
              onClick={async () => {
                if (!feedUrl) {
                  try {
                    const r = await calendarService.getMyFeed();
                    setFeedUrl(r.url);
                  } catch { /* interceptor */ }
                }
                setSubscribeOpen(true);
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface-alt px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:border-primary/30"
              title="Subscribe to this calendar from Google / Outlook / Apple"
            >
              Subscribe
            </button>
            <Button variant="primary" onClick={() => openNewModal()}><Plus size={14} /> New event</Button>
          </div>
        }
      />

      <div className="mx-auto grid w-full max-w-[1400px] gap-5 px-7 py-7 lg:grid-cols-[1fr_320px]">
        {/* ── Month grid ─────────────────────────────────────────── */}
        <Card padded={false}>
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <div className="flex items-center gap-2">
              <button onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1))} className="rounded-lg p-1.5 text-text-muted hover:bg-surface-alt"><ChevronLeft size={16} /></button>
              <button onClick={() => setMonthAnchor(new Date())} className="rounded-lg border border-border px-2.5 py-1 text-xs font-medium hover:bg-surface-alt">Today</button>
              <button onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1))} className="rounded-lg p-1.5 text-text-muted hover:bg-surface-alt"><ChevronRight size={16} /></button>
            </div>
            <div className="font-display text-base font-semibold text-text-primary">{monthLabel}</div>
            <div className="text-2xs text-text-muted">{loading ? 'Loading…' : `${events.length} event${events.length === 1 ? '' : 's'}`}</div>
          </div>

          <div className="grid grid-cols-7 border-b border-border bg-surface-alt/40">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="px-2 py-1.5 text-2xs font-semibold uppercase tracking-wide text-text-muted">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 border-b border-border">
            {grid.map((cell, i) => {
              const today = sameDay(cell.date, new Date());
              return (
                <button
                  key={i}
                  onClick={() => openNewModal(cell.date)}
                  className={clsx(
                    'group flex h-24 flex-col items-stretch border-b border-r border-border p-1 text-left transition-colors',
                    cell.inMonth ? 'bg-surface' : 'bg-surface-alt/40 text-text-muted',
                    'hover:bg-primary-soft/30',
                  )}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span className={clsx(
                      'text-xs font-medium',
                      today && 'flex h-5 w-5 items-center justify-center rounded-full bg-primary text-on-primary',
                    )}>
                      {cell.date.getDate()}
                    </span>
                  </div>
                  <div className="space-y-0.5 overflow-hidden">
                    {cell.events.slice(0, 2).map((e) => (
                      <div
                        key={e.eventUid}
                        onClick={(ev) => { ev.stopPropagation(); openEditModal(e); }}
                        className={clsx('truncate rounded border px-1 py-0.5 text-[10px] leading-tight cursor-pointer', TYPE_COLORS[e.type])}
                        title={e.title}
                      >
                        {new Date(e.startsAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} {e.title}
                      </div>
                    ))}
                    {cell.events.length > 2 && (
                      <div className="text-[10px] text-text-muted">+{cell.events.length - 2} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* ── Upcoming sidebar ───────────────────────────────────── */}
        <Card padded={false}>
          <div className="border-b border-border px-4 py-3 text-2xs font-semibold uppercase tracking-wide text-text-muted">
            Upcoming
          </div>
          {upcoming.length === 0 && (
            <div className="px-4 py-8 text-center text-xs text-text-muted">
              <CalendarIcon size={28} className="mx-auto mb-2 opacity-40" />
              No upcoming events
            </div>
          )}
          <div className="divide-y divide-border">
            {upcoming.map((e) => (
              <button
                key={e.eventUid}
                onClick={() => openEditModal(e)}
                className="flex w-full flex-col items-start gap-0.5 px-4 py-3 text-left hover:bg-surface-alt"
              >
                <div className="flex w-full items-center justify-between gap-2">
                  <span className="truncate text-[13px] font-semibold text-text-primary">{e.title}</span>
                  <span className={clsx('shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase', TYPE_COLORS[e.type])}>
                    {TYPE_LABELS[e.type]}
                  </span>
                </div>
                <div className="text-2xs text-text-muted">
                  {new Date(e.startsAt).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
                {e.location && (
                  <div className="flex items-center gap-1 text-2xs text-text-muted">
                    <MapPin size={9} /> {e.location}
                  </div>
                )}
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* ── Create / Edit modal ─────────────────────────────────── */}
      <Modal open={modalOpen} onClose={() => !busy && setModalOpen(false)} title={editing ? 'Edit event' : 'New event'} widthClass="max-w-lg">
        <div className="px-6 py-5">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Type">
              <Select value={type} onChange={(e) => setType(e.target.value as CalendarEventType)}>
                {(Object.keys(TYPE_LABELS) as CalendarEventType[]).map((t) => (
                  <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                ))}
              </Select>
            </FormField>
          </div>
          <FormField label="Title" required>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Appointment with…" />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Starts" required>
              <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </FormField>
            <FormField label="Ends" required>
              <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </FormField>
          </div>
          <FormField label="Location">
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Office / Zoom / etc." />
          </FormField>
          <FormField label="Description">
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </FormField>
        </div>
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          {editing ? (
            <Button variant="ghost" onClick={remove} disabled={busy}><Trash2 size={14} /> Delete</Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={busy} disabled={busy || !title.trim()}>{editing ? 'Save' : 'Create'}</Button>
          </div>
        </div>
      </Modal>

      <Modal open={subscribeOpen} onClose={() => setSubscribeOpen(false)} title="Subscribe to your calendar" widthClass="max-w-xl">
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-text-secondary">
            Add this URL to Google Calendar, Outlook, or Apple Calendar to see your Slightor events, deal close dates, and task due dates alongside your other calendars.
          </p>
          <div>
            <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted mb-1">Calendar feed URL</div>
            {feedUrl ? (
              <div className="flex items-stretch gap-2">
                <code className="flex-1 rounded-lg border border-border bg-surface-alt px-3 py-2 text-xs font-mono text-text-primary break-all">
                  {feedRevealed ? feedUrl : feedUrl.replace(/token=[^&]+/, 'token=••••••••')}
                </code>
                <button
                  onClick={() => setFeedRevealed((v) => !v)}
                  className="rounded-lg border border-border bg-surface-alt px-3 text-xs font-semibold text-text-secondary hover:border-primary/30"
                >{feedRevealed ? 'Hide' : 'Reveal'}</button>
                <button
                  onClick={() => navigator.clipboard.writeText(feedUrl).then(() => toast.success('Copied'))}
                  className="rounded-lg border border-primary/30 bg-primary-soft px-3 text-xs font-semibold text-primary hover:brightness-110"
                >Copy</button>
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-surface-alt px-3 py-2 text-xs text-text-muted">Loading…</div>
            )}
          </div>
          <div className="rounded-xl border border-border bg-surface-alt/40 p-4 text-xs text-text-secondary space-y-2">
            <div><strong>Google Calendar:</strong> Other calendars → "+" → From URL → paste the URL.</div>
            <div><strong>Outlook:</strong> Calendar → Add calendar → Subscribe from web → paste the URL.</div>
            <div><strong>Apple Calendar:</strong> File → New Calendar Subscription → paste the URL.</div>
          </div>
          <div className="flex items-center justify-between border-t border-border pt-3">
            <p className="text-2xs text-text-muted">Anyone with this URL can read your calendar. Rotate if leaked.</p>
            <button
              onClick={async () => {
                if (!window.confirm('Rotate the feed token? Subscribed apps will need the new URL.')) return;
                try {
                  const r = await calendarService.rotateMyFeed();
                  setFeedUrl(r.url); setFeedRevealed(true);
                  toast.success('New token generated');
                } catch { /* interceptor */ }
              }}
              className="rounded-lg border border-border bg-surface-alt px-3 py-1.5 text-xs font-semibold text-text-secondary hover:border-warning/40 hover:text-warning"
            >Rotate</button>
          </div>
        </div>
      </Modal>
    </>
  );
}
